/**
 * The validation status glyphs (WMC-565): ✓ on blue = validated (system),
 * ✓ on green = approved, ? on amber = review, ✕ on red = rejected, an empty
 * grey box = pending. The ✓ ? ✕ trio is lifted from the discography-IDs
 * certainty marker (CertaintyMarker.tsx) so the two columns match.
 */
import React from 'react';
import { Check, Loader2, Minus, X } from 'lucide-react';
import {
  AdminValidationStatus,
  STATUS_META,
  useValidationState,
  useValidationStore,
  validationStore,
  ValidationStatus,
} from './validationStore';

/** All three buttons are the same fixed square. 'sm' is the inline trio:
 *  three 20px squares and two 4px gaps span the "Validation" header label.
 *  'lg' is the floating bar, and the single status icon in validation mode. */
const SIZES = {
  sm: { box: 20, icon: 14 },
  lg: { box: 24, icon: 16 },
} as const;

/** "?" drawn on the same 24-unit grid as lucide's ✓ and ✕, so the three
 *  centre the same way in their boxes (a text "?" sits on the font's
 *  baseline and rides high). The paths are lucide's circle-help glyph,
 *  scaled up to the ✕'s height and re-centred; the stroke is scaled down by
 *  the same factor so it draws as thick as the other two. */
const QuestionMark: React.FC<{ size: number }> = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={4 / 1.3}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <g transform="translate(12 12.2) scale(1.3) translate(-12 -12)">
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </g>
  </svg>
);

/** The glyph drawn for a status; its colour is the status's `STATUS_META.bg`. */
const statusIcon = (status: ValidationStatus, px: number): React.ReactNode => {
  switch (status) {
    case 'validated':
    case 'approved':
      return <Check size={px} strokeWidth="4" />;
    case 'review':
      return <QuestionMark size={px} />;
    case 'rejected':
      return <X size={px} strokeWidth="4" />;
    default:
      return <Minus size={px} strokeWidth="3" className="tw-opacity-60" />;
  }
};

/** One non-interactive icon for the row's current status (validation mode,
 *  where the only way to change a status is the selection + floating bar;
 *  bulk edit, where the column is read-only). The grid uses 'lg' so the
 *  icon matches the floating bar's buttons. */
export const ValidationStatusIcon: React.FC<{
  rowId: string;
  size?: 'sm' | 'lg';
}> = ({ rowId, size = 'sm' }) => {
  const st = useValidationState(rowId);
  const { box, icon: px } = SIZES[size];
  const meta = STATUS_META[st.status];
  return (
    <span
      role="img"
      aria-label={meta.label}
      title={`${meta.label} · ${meta.hint}`}
      style={{ width: box, height: box }}
      className={[
        'tw-inline-flex tw-items-center tw-justify-center tw-rounded tw-text-white tw-flex-shrink-0',
        meta.bg,
      ].join(' ')}
    >
      {statusIcon(st.status, px)}
    </span>
  );
};

/**
 * ✓ ? ✕ for one row (`rowId`, the current status lit) or for a selection
 * (`rowIds`, nothing lit). `size` = 'sm' is the inline grid trio;
 * 'lg' is used by the floating bar. A click writes through the store.
 */
export const ValidationTrio: React.FC<{
  rowId?: string;
  rowIds?: string[];
  size?: 'sm' | 'lg';
  disabled?: boolean;
}> = ({ rowId, rowIds, size = 'sm', disabled }) => {
  const ids = rowIds ?? (rowId ? [rowId] : []);
  const st = useValidationState(rowId ?? '');
  /** The status being saved on this row (spinner on that button). */
  const savingStatus = useValidationStore((s) =>
    rowId ? s.busy[rowId] : undefined
  );
  /** Any of the rows mid-save: the trio is locked until it lands. */
  const saving = useValidationStore((s) => ids.some((id) => !!s.busy[id]));

  const { box, icon: px } = SIZES[size];
  const current = rowId ? st.status : null;

  const btn = (status: AdminValidationStatus, title: string) => {
    // A validated row shows its ✓ on the approve button, in the system's
    // blue: approving it is the admin taking the system's verdict over.
    const takesOverValidated = status === 'approved' && current === 'validated';
    return (
      <button
        type="button"
        title={
          takesOverValidated
            ? 'Validated by the system · click to approve'
            : title
        }
        disabled={disabled || ids.length === 0 || saving}
        onClick={(e) => {
          e.stopPropagation();
          void validationStore.setStatus(ids, status);
        }}
        style={{ width: box, height: box }}
        className={[
          'tw-flex tw-items-center tw-justify-center tw-p-0 tw-m-0 tw-rounded tw-cursor-pointer tw-text-white disabled:tw-opacity-40 disabled:tw-cursor-not-allowed',
          current === status
            ? STATUS_META[status].bg
            : takesOverValidated
            ? STATUS_META.validated.bg
            : 'tw-bg-gray-500 hover:tw-bg-gray-400',
        ].join(' ')}
      >
        {savingStatus === status ? (
          <Loader2 size={px} className="tw-animate-spin" />
        ) : (
          statusIcon(status, px)
        )}
      </button>
    );
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={[
        'tw-flex tw-items-center',
        size === 'lg' ? 'tw-gap-2' : 'tw-gap-1',
      ].join(' ')}
      onClick={(e) => e.stopPropagation()}
    >
      {btn('approved', 'Approve')}
      {btn('review', 'Mark for review')}
      {btn('rejected', 'Reject')}
    </div>
  );
};

/** Compact "N validated · N pending …" summary for the mode toolbar. */
export const ValidationCounts: React.FC<{
  counts: Record<ValidationStatus, number>;
  order: ValidationStatus[];
}> = ({ counts, order }) => (
  <span className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-textDove">
    {order.map((status) => (
      <span key={status} className="tw-inline-flex tw-items-center tw-gap-1">
        <span
          className={[
            'tw-inline-block tw-w-2 tw-h-2 tw-rounded-full',
            STATUS_META[status].bg,
          ].join(' ')}
        />
        <span className="tw-text-white">{counts[status]}</span>
        <span>{STATUS_META[status].label.toLowerCase()}</span>
      </span>
    ))}
  </span>
);
